import { RadioGroup, Tab } from "@headlessui/react";
import classNames from "classnames";
import { motion } from "framer-motion";
import { useRouter } from "next/router";
import { useTranslations } from "next-intl";
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";

import { useBreakpoint } from "~/contexts/breakpoint";
import { useFavouriteStore } from "~/contexts/favourite-store";
import { ErrorResponse } from "~/shared/api/error-response.class";
import { Coords } from "~/shared/coords.class";
import { AbstractShop } from "~/shared/shipping/abstract-shop.interface";
import crossCountryUtils from "~/utils/cross-country-utils";
import woosmapUtils, { WooosmapGeometry, WoosmapSuggestion } from "~/utils/woosmap-utils";

import Button from "../common/button";
import Icon from "../common/icon";
import Checkbox, { CheckboxVariants } from "../form/checkbox";
import LocalitiesAutocomplete from "../localities-autocomplete/localities-autocomplete";
import StockDetailsModal from "./stock-details-modal";
import { StoresMap } from "./stores-map";
import { calculateDistance, getOpeningHours, reverseGeocode } from "./stores-modal";
import styles from "./stores-modal.module.scss";

export const defaultLocation = { lat: undefined, lng: undefined };

export type ScrollGradient = { top: boolean; bot: boolean };

type StoresSelectionProps = {
  stores: AbstractShop[];
  searchCity?: string;
  locationPos: Coords;
  filterAvailableOnly?: boolean;
  onClose?: () => void;
  onSearchedCity?: Dispatch<SetStateAction<{ city: string; postal_code: string }>>;
  setLocationPos: Dispatch<SetStateAction<Coords>>;
  setFilterAvailableOnly?: Dispatch<SetStateAction<boolean>>;
  setPudoId?: (pudoId: string) => void;
  setStore?: (store: EVA.Core.ProductAvailabilityPickupOrganizationUnit) => void;
  initialStoreId?: number | string;
  errorMessage?: ErrorResponse | null;
  enabled?: boolean;
  isLoading?: boolean;
  inModal?: boolean;
  inPickup?: boolean;
  initPickupStoreId?: string;
  inPDP?: boolean;
};

export default function StoresSelection({
  stores,
  searchCity,
  locationPos,
  filterAvailableOnly,
  onClose,
  onSearchedCity,
  setLocationPos,
  setFilterAvailableOnly,
  setPudoId,
  errorMessage,
  enabled,
  isLoading,
  inModal,
  inPDP,
  inPickup,
  initPickupStoreId,
  setStore,
  initialStoreId,
}: StoresSelectionProps) {
  const t = useTranslations();
  const router = useRouter();
  const countryCode = crossCountryUtils.getCurrentCountryCode(router);

  const breakpoint = useBreakpoint();
  const { preferredStoreId, setPreferredStoreId } = useFavouriteStore();

  const [localStoreId, setLocalStoreId] = useState<string | number | null>(
    (inPickup ? initPickupStoreId : initialStoreId) || null
  );

  const storesList = useRef<HTMLElement>(null);
  const [selectedIndex, setSelectedIndex] = useState(localStoreId ? 1 : 0);
  const [listGradient, setListGradient] = useState<ScrollGradient>({ top: false, bot: false });
  const [autocompleteCoordinates, setAutocompleteCoordinates] = useState<WooosmapGeometry>();

  const [locationMessage, setLocationMessage] = useState("");

  function reverseGeocodeAndSearch(lat: string, lng: string) {
    if (lat && lng) {
      reverseGeocode(lat + "," + lng).then((addressDetails) => {
        const geocodedAddress: WoosmapSuggestion = addressDetails.results[0];
        // if country matches continue, otherwise show a custom error
        if (addressDetails.results.length > 0) {
          if (
            geocodedAddress.address_components.find((comp) => comp.types.includes("country"))?.short_name == countryCode
          ) {
            const address = woosmapUtils.mapSuggestionToAddressDto(geocodedAddress, countryCode);
            if (address.City && address.ZipCode) {
              onSearchedCity?.({ city: address.City, postal_code: address.ZipCode });
              const input = document.querySelector("#autocompleteInput") as HTMLInputElement;
              if (input) {
                input.value = `${geocodedAddress.formatted_address}`;
                setAutocompleteCoordinates(geocodedAddress.geometry);
              }
              setLocationMessage("");
            }
          } else {
            setLocationMessage(t("generic.location.wrongCountry"));
          }
        } else {
          setLocationMessage(t("generic.location.unableToGet"));
        }
      });
    }
  }

  // on first render call the setPudoId when inPickup and initPickupStoreId valorized
  useEffect(() => {
    if (inPickup && initPickupStoreId) {
      setPudoId?.(initPickupStoreId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // sync local store with the one in context when not in checkout (setStore is not valorized)
  useEffect(() => {
    if (!inPickup && setStore == undefined) {
      setLocalStoreId(preferredStoreId ?? null);
    }
  }, [preferredStoreId, inPickup, setStore]);

  // effect for scrolling into view when inModal
  useEffect(() => {
    const selectedStore = stores?.find((store) => `${store.ID}` == `${localStoreId}`);
    if (selectedStore) {
      document.querySelector(`#store-${localStoreId}`)?.scrollIntoView({ behavior: "instant", block: "center" });
    }
  }, [stores, localStoreId, inModal]);

  // this effect handles gradients on top/bottom of the scrollable container
  useEffect(() => {
    let currentList = storesList.current;

    const onScroll = () => {
      if (currentList) {
        if (currentList.scrollTop > 0) {
          // add gradient top
          setListGradient((curr) => ({ ...curr, top: true }));
        } else {
          // remove gradient top
          setListGradient((curr) => ({ ...curr, top: false }));
        }
        const diff = currentList.scrollHeight - currentList.offsetHeight;
        // threshold of 10px
        if (currentList.scrollTop >= diff - 10) {
          // remove gradient bottom
          setListGradient((curr) => ({ ...curr, bot: false }));
        } else {
          // add gradient bottom
          setListGradient((curr) => ({ ...curr, bot: true }));
        }
      }
    };

    if ((breakpoint == "mobile" && selectedIndex == 0) || breakpoint == "desktop") {
      let initInterval = setInterval(() => {
        if (storesList.current) {
          clearInterval(initInterval);
          currentList = storesList.current;
          if (currentList.scrollHeight > currentList.offsetHeight) {
            // add gradient - listener scroll
            onScroll();
            currentList.addEventListener("scroll", onScroll);
          } else {
            setListGradient({ top: false, bot: false });
          }
        }
      }, 100);
    } else {
      // remove gradient
      setListGradient({ top: false, bot: false });
    }

    return () => currentList?.removeEventListener("scroll", onScroll);
  }, [stores, selectedIndex, breakpoint]);

  function onLocationSuccess(position: { coords: { latitude: any; longitude: any } }) {
    const latitude = position.coords.latitude;
    const longitude = position.coords.longitude;
    setLocationPos({ lat: latitude, lng: longitude });
    reverseGeocodeAndSearch(latitude, longitude);
  }

  function onLocationError() {
    setLocationMessage(t("generic.location.unableToGet"));
  }

  function getLocation() {
    if (!navigator.geolocation) {
      setLocationMessage(t("generic.location.unsupported"));
    } else {
      setLocationMessage(t("generic.location.locating"));
      navigator.geolocation.getCurrentPosition(onLocationSuccess, onLocationError);
    }
  }

  function onSelectPoint(val: string | number) {
    setLocalStoreId(val);
    if (!inModal) {
      if (inPickup && setPudoId != undefined && typeof val == "string") {
        setPudoId(val);
      } else {
        if (setStore != undefined) {
          const selectedStore = stores?.find((store) => `${store.ID}` == `${val}`);
          setStore(selectedStore as EVA.Core.ProductAvailabilityPickupOrganizationUnit);
        } else {
          setPreferredStoreId(val);
        }
      }
    }
  }

  const radioGroup = (
    <div
      className={classNames(
        styles.storesRadioWrapper,
        { [styles.topGradient]: listGradient.top },
        { [styles.botGradient]: listGradient.bot }
      )}
    >
      <RadioGroup value={`${localStoreId}`} onChange={onSelectPoint} className={styles.storesRadio} ref={storesList}>
        {stores?.map((store) => (
          <RadioGroup.Option
            key={store.ID}
            disabled={store.HasStock != undefined && store.HasStock == false && !inPDP}
            value={`${store.ID}`}
            className={styles.pickup}
            id={`store-${store.ID}`}
          >
            {({ checked, disabled }) => (
              <>
                <Icon
                  name={disabled ? "radio-unselectable" : checked ? "radio-selected" : "radio-unselected"}
                  width={16}
                  height={16}
                />
                <div className={styles.pickupContainer}>
                  <div className={styles.pickupRow}>
                    <RadioGroup.Label className={styles.pickupName}>{store.Name}</RadioGroup.Label>
                    {preferredStoreId == store.ID && (
                      <Icon width={16} height={16} name="star-full" className={styles.preferredStore} />
                    )}
                    {(store.DistanceInKm ||
                      (locationPos.lat &&
                        locationPos.lng &&
                        (store.Latitude || store.Address.Latitude) &&
                        (store.Longitude || store.Address.Longitude))) && (
                      <span className={styles.pickupDistance}>
                        {(store.DistanceInKm
                          ? store.DistanceInKm
                          : calculateDistance(
                              locationPos!.lat!,
                              locationPos!.lng!,
                              (store.Latitude || store.Address.Latitude)!,
                              (store.Longitude || store.Address.Longitude)!
                            )
                        )
                          .toFixed(1)
                          .replace(".", ",")}{" "}
                        km
                      </span>
                    )}
                  </div>
                  <RadioGroup.Description className={styles.pickupAddress}>
                    {store.Address?.Address1}
                    {store.Address?.HouseNumber ? ` ${store.Address.HouseNumber}` : ""}
                    {store.Address?.ZipCode ? `, ${store.Address.ZipCode}` : ""}
                    {store.Address?.City ? ` ${store.Address.City}` : ""}
                    {store.Address?.State ? ` ${store.Address.State}` : ""}
                    {/* {store.Address?.CountryID ? `, ${store.Address.CountryID}` : ""} */}
                    {store.Address?.PhoneNumber ? (
                      <>
                        {` · `}
                        <a href={`tel:${store.Address.PhoneNumber}`}>{store.Address.PhoneNumber}</a>
                      </>
                    ) : (
                      ""
                    )}
                  </RadioGroup.Description>
                  {store.HasStock != undefined && (
                    <div className={styles.stock}>
                      <div
                        className={styles.stockIndicator}
                        style={{ backgroundColor: store.HasStock ? "green" : "#CC0C0C" }}
                      />
                      <p className={styles.stockText}>
                        {store.HasStock
                          ? t("generic.productAvailableAtThisLocation")
                          : t("generic.productUnavailableAtThisLocation")}
                      </p>
                    </div>
                  )}
                  {!inPickup && !store.HasStock && !inModal && <StockDetailsModal store={store} />}
                  {(store.RegularOpeningHours || store.OpeningHours) &&
                    getOpeningHours(
                      (store.RegularOpeningHours || store.OpeningHours) as EVA.Core.RegularOpeningHours[],
                      router.locale,
                      t("generic.storeOpeningHours")
                    )}
                  {store.customOpening && (
                    <div className={classNames(styles.hours, styles.hoursContent)}>{store.customOpening}</div>
                  )}
                </div>
              </>
            )}
          </RadioGroup.Option>
        ))}
        {stores?.length == 0 && (
          <p className={styles.noresults}>
            {errorMessage
              ? t(`generic.${errorMessage.messages?.[0].message}`, { city: searchCity })
              : t("generic.noresults")}
          </p>
        )}
      </RadioGroup>
    </div>
  );

  const intOnClose = () => {
    onClose?.();
    setFilterAvailableOnly?.(false);
    setAutocompleteCoordinates(undefined);
  };

  // only present in modal
  const confirmSelection = (
    <Button
      className={styles.confirmPickupBtn}
      variant="primary"
      disabled={!localStoreId}
      onClick={() => {
        intOnClose();
        if (!!localStoreId) {
          if (inPickup && typeof localStoreId == "string") {
            setPudoId?.(localStoreId);
          } else {
            setPreferredStoreId(localStoreId);
          }
        }
      }}
    >
      {t("generic.confirmStoreSelection")}
    </Button>
  );

  return (
    <>
      {inPickup && <p className={styles.typeToStart}>{t("generic.checkout.pickupStart")}</p>}
      <div className={styles.searchRow}>
        {inPickup ? (
          <LocalitiesAutocomplete
            label={t("generic.searchCity")}
            placeholder={t("generic.searchCityPlaceholder")}
            onSelectCity={(selectedCity) => {
              setLocationMessage("");
              onSearchedCity?.(selectedCity);
            }}
            onSelectCoordinates={(coords) => {
              setAutocompleteCoordinates(coords);
              setLocationPos({ lat: coords.location.lat, lng: coords.location.lng });
            }}
            resultType="locality|postal_code"
            informativeMessage={locationMessage}
            externalLoading={isLoading}
          />
        ) : (
          <LocalitiesAutocomplete
            label={t("generic.searchCity")}
            placeholder={t("generic.searchCityPlaceholder")}
            resultType="locality|postal_code|address"
            onSelectCoordinates={(coords) => {
              setLocationMessage("");
              setAutocompleteCoordinates(coords);
              setLocationPos({ lat: coords.location.lat, lng: coords.location.lng });
            }}
            informativeMessage={locationMessage}
            externalLoading={isLoading}
          />
        )}
        <button type="button" onClick={() => getLocation()} className={styles.geo}>
          <Icon name="geolocation" />
        </button>
      </div>
      {filterAvailableOnly != undefined && (
        <Checkbox
          className={styles.pickupFilter}
          checked={filterAvailableOnly}
          onChange={(e) => setFilterAvailableOnly?.(e.target.checked)}
          label={t("generic.storesavailability")}
          variant={CheckboxVariants.toggle}
        />
      )}
      {inPickup && !enabled ? (
        <></>
      ) : breakpoint === "mobile" ? (
        <>
          <Tab.Group selectedIndex={selectedIndex} onChange={setSelectedIndex}>
            <Tab.List className={styles.tabList}>
              <Tab className={styles.tab}>
                {({ selected }) => (
                  <>
                    <p>{t("generic.listview")}</p>
                    {selected && <motion.div className={styles.selectedBg} layoutId="selectedBg" />}
                  </>
                )}
              </Tab>
              <Tab className={styles.tab}>
                {({ selected }) => (
                  <>
                    <p>{t("generic.mapview")}</p>
                    {selected && <motion.div className={styles.selectedBg} layoutId="selectedBg" />}
                  </>
                )}
              </Tab>
            </Tab.List>
            <Tab.Panels className={classNames(styles.tabPanels, { [styles.inModal]: inModal })}>
              <Tab.Panel className={styles.tabPanel}>{radioGroup}</Tab.Panel>
              <Tab.Panel className={styles.tabPanel}>
                <StoresMap
                  stores={stores}
                  location={locationPos}
                  selectedStoreID={localStoreId}
                  onSelectStore={onSelectPoint}
                  inPickUp={inPickup}
                  flyTo={autocompleteCoordinates}
                  inModal={inModal}
                  inPDP={inPDP}
                />
              </Tab.Panel>
            </Tab.Panels>
          </Tab.Group>
          {inModal && confirmSelection}
        </>
      ) : (
        <div className={styles.desktopWrapper}>
          <StoresMap
            stores={stores}
            location={locationPos}
            selectedStoreID={localStoreId}
            onSelectStore={onSelectPoint}
            inPickUp={inPickup}
            flyTo={autocompleteCoordinates}
            inModal={inModal}
            inPDP={inPDP}
          />
          <div className={styles.tabPanelDesktop}>
            {radioGroup}
            {inModal && confirmSelection}
          </div>
        </div>
      )}
    </>
  );
}
